// src/pages/CouponPrint.js
// COUPON MAXULA — page publique imprimable, format A5 paysage (moitie d'un A4).
//
// Pourquoi une page HTML et non un PDF genere par le serveur :
// le bas du coupon porte les conditions generales EN ARABE. La librairie PDF du
// backend (PDFKit) ne sait ni relier les lettres arabes ni gerer le sens de
// lecture — le texte sortirait en caracteres detaches et a l'envers, et il
// faudrait embarquer une police arabe plus une librairie de mise en forme. Le
// navigateur fait tout cela nativement, avec la police Cairo deja chargee par
// l'application. L'impression passe par la boite de dialogue du navigateur,
// reglee sur A5 paysage via `@page`.
//
// LE COUPON NE PORTE AUCUN PRIX — c'est voulu : il s'offre. Le montant vit sur
// la facture, document distinct. Le serveur ne renvoie meme pas le montant sur
// cette route (liste blanche cote controleur), donc aucune modification de
// cette page ne pourrait le faire apparaitre par accident.
//
// Maxula — AbodyStudio Limited

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiPrinter, FiAlertTriangle, FiLoader, FiEdit3, FiGlobe, FiPhone, FiLock, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';
import API from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { normalizeImagePath } from '../utils/imageUtils';

const PRINT_CSS = `
@page { size: A5 portrait; margin: 0; }

.coupon-sheet {
  /* A5 PORTRAIT : 148 x 210 mm. Les dimensions sont en millimetres et non en
     pixels — a l'impression, seul le millimetre a un sens, un px depend du
     zoom d'ecran et le document deborderait sur une seconde page.
     HAUTEUR FIXE et non min-height : avec min-height, un contenu un peu long
     pousse la feuille au-dela de 210 mm et l'imprimante ouvre une deuxieme
     page pour quelques millimetres. */
  width: 148mm;
  height: 210mm;
  overflow: hidden;
  padding: 6mm 6mm;
  /* Colonne flex : le bloc promotionnel porte margin-top:auto et se colle donc
     au bas de la feuille quelle que soit la longueur de la description. Sans
     cela il flotterait juste sous les conditions, avec un grand vide dessous
     sur les offres au titre court. */
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #111;
  font-family: 'Cairo', 'Segoe UI', Tahoma, Arial, sans-serif;
  font-size: 8pt;
  line-height: 1.35;
  box-sizing: border-box;
}
.coupon-qr {
  /* 24 mm : en dessous de 20 mm, un QR imprime a 300 dpi devient penible a
     lire pour un telephone tenu a bout de bras au comptoir. */
  width: 24mm; height: 24mm; display: block;
}
.coupon-rule { border: 0; border-top: 1px solid #cfcfcf; margin: 2.2mm 0; }
.coupon-title { font-size: 12.5pt; font-weight: 800; margin: 0 0 2mm; }
.coupon-meta td { padding: 0.8mm 0; font-size: 8.8pt; }
.coupon-meta td:first-child { color: #333; }
.coupon-meta td:last-child { text-align: right; font-weight: 600; }
.coupon-benef {
  text-align: center; font-size: 10.5pt; font-weight: 800;
  border-top: 1px solid #cfcfcf; padding-top: 2.5mm; margin-top: 2mm;
}
.coupon-section { font-size: 9pt; letter-spacing: .5px; padding: 1mm 0 1mm 2mm; }
.coupon-deal { font-size: 11pt; font-weight: 800; margin: 1.5mm 0 1mm; }
.coupon-ar {
  direction: rtl;
  text-align: right;
  font-size: 7.4pt;
  line-height: 1.65;
  /* unicode-bidi: isolate — sans lui, une ligne arabe qui contient un chiffre
     ou un signe de ponctuation latin fait deborder le sens de lecture sur le
     texte voisin : la ponctuation saute a l'autre bout de la ligne. */
  unicode-bidi: isolate;
  /* Pile de polices ARABES et non latines : Helvetica ou Segoe UI n'ont aucun
     glyphe arabe et le navigateur afficherait des rectangles vides. Cairo est
     charge par index.css ; les suivantes existent en natif sur Windows, macOS
     et Android, donc le texte reste lisible meme sans acces a Google Fonts. */
  font-family: 'Cairo', 'Noto Naskh Arabic', 'Segoe UI Arabic', 'Geeza Pro', Tahoma, sans-serif;
}

@media print {
  /* Chrome et Safari suppriment les couleurs de fond a l'impression par
     defaut : sans cette regle, le bandeau gris DESCRIPTION sort invisible et
     le coupon perd sa structure. */
  .coupon-sheet, .coupon-sheet * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* LA CAUSE DES PAGES BLANCHES SUPPLEMENTAIRES.
     La propriete visibility:hidden rend invisible mais NE LIBERE PAS LA PLACE : tout le
     reste de l'application continuait d'occuper sa hauteur, et l'imprimante
     sortait le coupon suivi de plusieurs pages vides. Il faut donc en plus
     borner html et body a la taille exacte de la feuille et couper ce qui
     depasse. */
  html, body {
    width: 148mm !important;
    height: 210mm !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: #fff !important;
  }
  body * { visibility: hidden !important; }
  .coupon-sheet, .coupon-sheet * { visibility: visible !important; }
  .coupon-sheet {
    position: absolute !important;
    left: 0; top: 0;
    margin: 0 !important;
    box-shadow: none !important;
    /* Interdit toute coupure interne : un saut de page au milieu du bloc
       arabe ouvrirait une seconde feuille pour trois lignes. */
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .no-print { display: none !important; }
}
`;

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Conditions generales arabes du coupon Maxula. Elles sont ici et non en base :
// ce sont des mentions contractuelles de la plateforme, identiques sur tous les
// coupons. Les conditions PROPRES a l'offre viennent, elles, du snapshot du
// coupon (champ `conditions`) et s'affichent plus haut.
// Conditions generales du coupon, par PAIRES : la clause arabe puis sa version
// francaise, l'une sous l'autre. Chaque paire dit la meme chose dans les deux
// langues — si vous modifiez une clause, modifiez les DEUX lignes de la paire.
// Deux versions divergentes du meme contrat sur le meme papier, c'est le litige
// garanti.
//
// Les paires sont rendues avec `dir` explicite ligne par ligne : melanger de
// l'arabe et du francais dans un meme bloc sans isolation fait sauter la
// ponctuation d'un bout a l'autre de la ligne.
const CGV_PAIRS = [
  {
    ar: 'مهم جداً: مكسولا (Maxula) منصة إشهار ودليل مدن، وليست مقدمة للخدمة. الخدمة مسؤولية الشريك المذكور في القسيمة.',
    fr: "Important : Maxula est une plateforme publicitaire et un City Guide, et n'est pas le prestataire du service. La prestation relève de la responsabilité du partenaire indiqué sur le coupon.",
  },
  {
    ar: 'القسيمة صالحة 60 يوماً، غير قابلة للاسترجاع ويمكن إهداؤها لشخص آخر.',
    fr: 'Le coupon est valable 60 jours, non remboursable et transférable.',
  },
  {
    ar: 'في حالة القوة القاهرة، يمكن قبول الاسترجاع استثنائياً مع خصم 12% كمصاريف إدارية.',
    fr: 'En cas de force majeure, un remboursement peut être accepté exceptionnellement avec 12 % de frais administratifs.',
  },
  {
    ar: 'في حالة القبول، يتم الاسترجاع خلال 15 إلى 45 يوماً.',
    fr: "En cas d'acceptation, le remboursement est effectué sous 15 à 45 jours.",
  },
];

const CouponPrint = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  // Logo du site, comme sur l'en-tete public : on prend celui saisi dans
  // Admin > Parametres, avec repli sur /logo.png. Le texte "Maxula.ma" reste
  // affiche en dessous — si l'image manque ou n'est pas chargee par
  // l'imprimante, le coupon ne doit pas sortir sans identite.
  // La page du coupon est PUBLIQUE : elle s'ouvre sans compte, c'est ce qui
  // permet de l'envoyer par WhatsApp ou de la retrouver en scannant le QR.
  // Le bouton de modification du texte ne doit donc PAS y etre visible pour
  // tout le monde — un client qui scanne son coupon pouvait reecrire le titre
  // de la prestation et les horaires, puis imprimer le resultat.
  // Reserve aux comptes INTERNES : l'admin et le personnel (commerciaux,
  // agents guichet), qui preparent les coupons au comptoir.
  //
  // Pourquoi le role suffit ici, sans verifier une permission precise : cette
  // page est publique et l'API ne transmet pas au navigateur la liste des
  // droits d'un compte 'staff'. Le controle vise un CLIENT qui scanne son
  // coupon, pas un arbitrage entre membres de l'equipe — et un compte du
  // personnel n'existe que parce que vous l'avez cree.
  //
  // Le marchand n'est PAS inclus : il pourrait reecrire les conditions de sa
  // propre prestation sur le document remis a l'acheteur.
  const { user } = useAuth() || {};
  const role = String(user?.role || '');
  const canEdit = role === 'admin' || role === 'staff';
  // CONFIRMATION DE PRESTATION.
  //
  // Le partenaire scanne le QR du coupon avec l'appareil photo de son
  // telephone : le QR contient deja l'URL complete de cette page. Aucune
  // application ni bibliotheque de scan a installer.
  //
  // La securite ne vient pas du scan mais de l'AUTHENTIFICATION : le bouton
  // n'apparait qu'a un compte connecte (marchand, personnel ou admin), et le
  // serveur revalide de toute facon que le coupon appartient bien a cet
  // etablissement. Un client qui scanne son propre coupon ne voit rien.
  const canConfirm = role === 'admin' || role === 'staff' || role === 'merchant';
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const { settings } = useSettings() || {};
  const logoSrc =
    (settings?.logo ? normalizeImagePath(settings.logo) : null) ||
    `${process.env.PUBLIC_URL || ''}/logo.png`;

  const [logoOk, setLogoOk] = useState(true);


  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [editing, setEditing] = useState(false);

  // Deconnexion ou changement de compte pendant que le panneau est ouvert :
  // on le referme, sinon les champs resteraient a l'ecran. Place APRES la
  // declaration de setEditing — un effet ecrit au-dessus fonctionnerait par
  // chance (il ne s'execute qu'apres le rendu) mais se lit comme une erreur.
  useEffect(() => {
    if (!canEdit) setEditing(false);
  }, [canEdit]);
  const [text, setText] = useState({ title: '', description: '', partner: '', hours: '' });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await API.get(`/vouchers/public/${encodeURIComponent(code)}`, { params: { t: token } });
        const d = res?.data?.data;
        if (!mounted) return;
        if (!d) throw new Error('Coupon introuvable');
        setState({ loading: false, error: null, data: d });
        setText({
          title: d.dealTitle || '',
          // La DESCRIPTION est la description de la prestation, PAS le tableau
          // `conditions` de l'offre : celui-ci contient une quinzaine de regles
          // d'usage bilingues qui remplissaient toute la feuille et repoussaient
          // les conditions generales Maxula hors de la page.
          description: d.dealDescription || '',
          partner: [
            d.merchantName ? `Partenaire : " ${d.merchantName} "` : '',
            d.merchantAddress ? `Adr : ${d.merchantAddress}` : ''
          ].filter(Boolean).join('   '),
          hours: d.instructions || '',
        });
      } catch (e) {
        if (!mounted) return;
        const msg = e?.response?.data?.message || e.message || 'Erreur de chargement';
        setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => { mounted = false; };
  }, [code, token]);

  const { loading, error, data } = state;

  // L'URL de l'image QR est construite depuis la base de l'API et non ecrite en
  // dur : le backend vit sur le meme domaine en production mais sur un autre
  // port en local, et un chemin fixe casserait dans l'un des deux cas.
  const qrUrl = useMemo(() => {
    if (!data?.code) return null;
    const base = String(API?.defaults?.baseURL || '/api').replace(/\/+$/, '');
    // link=1 : le QR encode l'URL COMPLETE du coupon et non le code seul, pour
    // que le porteur retrouve son coupon sur son telephone en le scannant.
    // Le jeton est transmis parce que le serveur l'exige pour cette forme —
    // sans quoi on pourrait fabriquer un QR ouvrant le coupon d'un code devine.
    return `${base}/vouchers/qr/${encodeURIComponent(data.code)}?link=1&t=${encodeURIComponent(token)}`;
  }, [data, token]);

  // Validite : la DATE d'echeance ET la duree en clair. Le coupon dure 60 jours
  // (2 mois) a compter de l'achat ; afficher seulement une date oblige le
  // porteur a compter, et afficher seulement "2 mois" laisse discuter le point
  // de depart. Les deux ensemble ne se discutent pas.
  const validityLine = useMemo(() => {
    if (!data?.expiresAt) return null;
    return `Bon valable 60 jours (jusqu'au ${fmtDate(data.expiresAt)})`;
  }, [data]);


  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-2 text-gray-500">
        <FiLoader className="animate-spin" /> Chargement du coupon…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-red-100 rounded-2xl shadow p-6 text-center">
          <span className="text-red-500 inline-flex"><FiAlertTriangle size={26} /></span>
          <h1 className="mt-2 text-lg font-bold text-gray-900">Coupon indisponible</h1>
          <p className="mt-1 text-sm text-gray-600">{error}</p>
          <p className="mt-3 text-xs text-gray-400">
            Support : Support@abodystudio.com — WhatsApp +212663033383
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-6">
      <style>{PRINT_CSS}</style>

      <div className="no-print max-w-[148mm] mx-auto px-4 mb-4 flex flex-wrap gap-2 items-center justify-between">
        <div className="text-sm text-gray-600">
          {canEdit ? (
            <>
              Format A5 portrait — dans la fenêtre d'impression : <strong>A5</strong>,{' '}
              <strong>Portrait</strong>, marges <strong>Aucune</strong>, échelle{' '}
              <strong>100 %</strong>.
            </>
          ) : (
            // Le client, lui, n'a pas a lire une consigne de reglage
            // d'imprimante : il veut surtout garder son coupon sur son
            // telephone.
            <>Conservez ce coupon et présentez-le au partenaire.</>
          )}
        </div>
        <div className="flex gap-2">
          {/* CONFIRMER LA PRESTATION — c'est ce geste, et lui seul, qui
              declenche le paiement du partenaire. Tant qu'il n'a pas eu lieu,
              un coupon achete mais jamais utilise ne lui rapporte rien. */}
          {canConfirm && data && !confirmed && String(data.status || 'active') === 'active' && (
            <button
              type="button"
              disabled={confirming}
              onClick={async () => {
                if (!window.confirm('Confirmer que le client s\'est présenté et que la prestation a été rendue ?\n\nCette action est définitive.')) return;
                setConfirming(true);
                try {
                  // La route est en GET (voir routes/voucherRoutes.js), pas en
                  // POST : j'avais suppose la methode au lieu de la verifier,
                  // d'ou le « Route not found ».
                  const r = await API.get(`/vouchers/validate/${encodeURIComponent(data.code)}`);
                  setConfirmed(true);
                  toast.success(r?.data?.message || 'Prestation confirmée');
                } catch (e) {
                  toast.error(e?.response?.data?.message || 'Confirmation refusée');
                } finally {
                  setConfirming(false);
                }
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-60"
            >
              <FiCheckCircle size={15} /> {confirming ? 'Confirmation…' : 'Confirmer la prestation'}
            </button>
          )}
          {confirmed && (
            <span className="inline-flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-sm font-bold text-green-700">
              <FiCheckCircle size={15} /> Prestation confirmée
            </span>
          )}
          {canEdit ? (
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FiEdit3 size={15} /> {editing ? 'Terminer la modification' : 'Modifier le texte'}
            </button>
          ) : null}
          {/* Sur telephone, la boite d'impression propose "Enregistrer au format
              PDF" : c'est aussi le bouton de telechargement, inutile d'en
              ajouter un second qui ferait la meme chose. */}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#e11d7b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#c4176a]"
          >
            <FiPrinter size={15} /> Imprimer / Télécharger
          </button>
        </div>
      </div>

      {/* Double garde-fou : `canEdit` est reteste ici et pas seulement sur le
          bouton. Sinon un etat `editing` reste a true (changement de compte,
          deconnexion sans rechargement) laisserait le panneau ouvert. */}
      {editing && canEdit ? (
        // Modification a la volee : le texte imprime n'est pas toujours celui
        // saisi a la creation de l'offre (precision demandee par le client au
        // guichet, mention de derniere minute). Les changements restent LOCAUX
        // a cette impression et ne touchent pas la base : le coupon est un
        // contrat fige, on n'en reecrit pas le contenu enregistre.
        <div className="no-print max-w-[148mm] mx-auto px-4 mb-4 grid gap-3">
          {[
            ['title', 'Titre de la prestation'],
            ['description', 'Description / conditions (une par ligne)'],
            ['partner', 'Ligne partenaire'],
            ['hours', 'Horaires et consignes'],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="block text-xs font-semibold text-gray-500 mb-1">{label}</span>
              <textarea
                value={text[key]}
                onChange={(e) => setText((t) => ({ ...t, [key]: e.target.value }))}
                rows={key === 'description' ? 4 : 2}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </label>
          ))}
          <p className="text-xs text-gray-500">
            Ces modifications ne concernent que l'impression en cours. Le coupon
            enregistré n'est pas modifié.
          </p>
        </div>
      ) : null}

      {/* ================= LA FEUILLE ================= */}
      <div className="coupon-sheet mx-auto shadow-lg">
        {/* En-tete sur deux colonnes, comme le coupon de reference. En portrait
            la largeur utile tombe a ~136 mm : l'ecart entre colonnes est reduit
            a 4 mm, sinon le nom du marchand et les dates se retrouvent a la
            ligne dans le tableau de droite. */}
        <div style={{ display: 'flex', gap: '4mm', alignItems: 'flex-start' }}>
          {/* Colonne gauche : identite Maxula */}
          <div style={{ width: '46%' }}>
            {/* Logo du site. Le nom en texte a ete retire : le logo le porte
                deja, l'afficher en plus faisait doublon.
                Il reste toutefois en RESERVE : si l'image echoue a charger
                (logo supprime des parametres, blocage reseau), le texte prend
                le relais. Un coupon remis a un client ne doit jamais sortir
                sans identite. */}
            {logoOk ? (
              <img
                src={logoSrc}
                alt="Maxula.ma"
                style={{ height: '12mm', width: 'auto', display: 'block', marginBottom: '1.5mm' }}
                onError={() => setLogoOk(false)}
              />
            ) : (
              <div style={{ fontSize: '15pt', fontWeight: 800, color: '#e11d7b', lineHeight: 1, marginBottom: '1mm' }}>
                Maxula.ma
              </div>
            )}
            <div style={{ fontSize: '7.5pt', fontWeight: 700, letterSpacing: '.3px', color: '#333' }}>
              PLATEFORME PUBLICITAIRE &amp; CITY GUIDE
            </div>
            {/* La ligne « Travel | Immobilier | Evénementiel » a ete retiree :
                Immobilier et Voyages figurent deja dans la ligne d'activites
                juste en dessous, le lecteur lisait deux fois la meme chose.
                Le bloc d'activites remonte donc contre la signature. */}

            <div style={{ marginTop: '1.5mm', fontSize: '7.4pt' }}>
              <div>Deals | Shopping | Services | Marketing | Voyages | Immobilier</div>
              <div className="coupon-ar" lang="ar" dir="rtl" style={{ fontSize: '7.4pt' }}>
                عروض | تسوق | خدمات | أسفار | وكالة عقارية
              </div>
              <div>Tanger | Casablanca | Rabat | Fes | Marrakech</div>
              <div style={{ fontWeight: 700, marginTop: '1mm' }}>
                Service Clients : 05.39.34.24.94 / 06.06.06.61.62
              </div>
              <div>contact@maxula.ma / www.maxula.ma</div>
            </div>
          </div>

          {/* Colonne droite : identifiant et dates */}
          <div style={{ width: '54%' }}>
            <div className="coupon-title">COUPON {data.code}</div>
            <hr className="coupon-rule" />
            <table className="coupon-meta" style={{ width: '100%' }}>
              <tbody>
                <tr><td>Date</td><td>{fmtDate(data.purchasedAt)}</td></tr>
                <tr><td>Vendeur</td><td>{data.merchantName || 'Administration'}</td></tr>
                <tr><td>Échéance</td><td>{fmtDate(data.expiresAt)}</td></tr>
              </tbody>
            </table>
            {/* Repli sur un tiret et NON sur « Maxula » : ecrire le nom de la
                plateforme sur le coupon d'un client est pire que ne rien
                ecrire, le partenaire croit que le coupon appartient a l'agence.
                Un tiret se voit et se corrige a la main si besoin. */}
            <div className="coupon-benef">
              Bénéficiaire : {data.beneficiaire || '—'}
            </div>
          </div>
        </div>

        <hr className="coupon-rule" />
        <div className="coupon-section" style={{ background: '#f4f4f4' }}>DESCRIPTION</div>
        <hr className="coupon-rule" style={{ marginTop: '1mm' }} />

        <div className="coupon-deal">{text.title || '—'}</div>

        {text.description
          ? text.description.split('\n').filter(Boolean).map((line, i) => (
              <div key={i} style={{ color: '#333' }}>{line}</div>
            ))
          : null}

        <div style={{ margin: '2mm 0 1mm', color: '#666' }}>---------</div>

        {text.partner ? <div>{text.partner}</div> : null}
        {data.merchantPhone ? <div>Tél : {data.merchantPhone}</div> : null}
        {/* Horaires et validite sur UNE ligne, separes par un slash, comme sur
            le coupon de reference. */}
        {(text.hours || validityLine) ? (
          <div>
            {text.hours ? `• ${text.hours}` : ''}
            {text.hours && validityLine ? ' / ' : ''}
            {validityLine ? `• ${validityLine}` : ''}
          </div>
        ) : null}
        {data.status === 'used' ? (
          <div style={{ marginTop: '1.5mm', fontWeight: 800, color: '#b91c1c' }}>
            COUPON DÉJÀ UTILISÉ — {fmtDate(data.usedAt)}
          </div>
        ) : null}

        {/* Conditions generales sur DEUX COLONNES, francais et arabe.
            Cote a cote plutot qu'empilees : sur une A5 deja dense, deux colonnes
            tiennent dans la hauteur qu'une seule version prenait en pleine
            largeur. lang + dir sont portes par l'element et pas seulement par le
            CSS — le moteur d'impression ignore parfois une partie de la feuille
            de style, et l'arabe repasserait de gauche a droite. */}
        {/* Conditions generales EN ARABE UNIQUEMENT, comme sur le coupon de
            reference. Une colonne francaise en regard avait ete essayee : elle
            double la hauteur du bloc et fait deborder la feuille sur une
            deuxieme page. Le document doit tenir sur UNE A5, c'est la
            contrainte qui prime. */}
        {/* Conditions generales : chaque clause en arabe, puis sa version
            francaise juste dessous. */}
        <div style={{ marginTop: '2mm' }}>
          {CGV_PAIRS.map((c, i) => (
            <div key={i} style={{ marginBottom: '1.2mm' }}>
              <div
                className="coupon-ar"
                lang="ar"
                dir="rtl"
                style={i === 0 ? { fontWeight: 700 } : undefined}
              >
                {c.ar}
              </div>
              <div
                lang="fr"
                dir="ltr"
                style={{
                  fontSize: '6.8pt',
                  lineHeight: 1.4,
                  color: '#333',
                  ...(i === 0 ? { fontWeight: 700 } : {}),
                }}
              >
                {c.fr}
              </div>
            </div>
          ))}
        </div>

        {/* QR — il encode l'URL COMPLETE du coupon : le porteur le scanne et
            retrouve son coupon sur son telephone, ou il peut le retelecharger.
            Le lien porte un jeton, donc qui photographie le papier peut rouvrir
            le coupon — mais un coupon est un document AU PORTEUR : cette
            personne a de toute facon deja le code, qui suffit a le consommer.
            Le risque n'augmente pas, le service oui.
            L'image vient de la route publique /api/vouchers/qr/:code (deja
            utilisee par les emails) — aucune librairie ajoutee au bundle. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4mm', marginTop: '2.5mm' }}>
          <img
            className="coupon-qr"
            src={qrUrl}
            alt={`QR ${data.code}`}
            /* Si la route QR est indisponible (librairie qrcode absente du
               serveur), on masque l'image au lieu d'imprimer une icone d'image
               cassee au milieu du coupon. */
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div style={{ fontSize: '7pt', lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, fontSize: '8pt' }}>{data.code}</div>
            <div>Scannez pour ouvrir et télécharger votre coupon.</div>
            <div className="coupon-ar" lang="ar" dir="rtl" style={{ fontSize: '7pt' }}>
              امسح الرمز لفتح القسيمة وتحميلها.
            </div>
            {/* Le code reste ecrit a cote du QR : papier plie, encre qui bave,
                telephone qui ne lit pas — le partenaire doit pouvoir le saisir
                a la main dans son espace de validation. */}
            <div style={{ marginTop: '0.8mm' }}>Code à présenter au partenaire.</div>

            {/* Usage unique — mention placee CONTRE le QR et non dans les
                conditions du bas : c'est au moment ou le porteur regarde son
                code qu'il faut lui dire qu'il ne servira qu'une fois.
                Cadenas dessine en SVG et non emoji : un emoji sort en carre
                vide sur beaucoup d'imprimantes noir et blanc. */}
            <div
              style={{
                marginTop: '1.2mm',
                paddingTop: '1mm',
                borderTop: '1px dashed #cfcfcf',
                fontWeight: 700,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.2mm' }}>
                <FiLock size={9} style={{ marginTop: '0.6mm', flexShrink: 0 }} />
                <span>Coupon et code à usage unique — valables une seule fois.</span>
              </div>
              <div className="coupon-ar" lang="ar" dir="rtl" style={{ fontSize: '7pt', fontWeight: 700 }}>
                القسيمة والرمز صالحان للاستعمال مرة واحدة فقط.
              </div>
            </div>
          </div>
        </div>

        {/* BLOC PROMOTIONNEL — colle en bas de feuille (margin-top:auto).
            Un coupon est souvent offert : celui qui le recoit ne connait pas
            forcement Maxula. Ces quatre lignes sont la seule occasion de lui
            dire ou trouver d'autres offres, et de recruter un partenaire.
            Icones FontAwesome / Feather et non emojis : un emoji ne s'imprime
            pas de la meme facon d'une imprimante a l'autre, et sort souvent en
            carre vide en noir et blanc. */}
        <div
          style={{
            marginTop: 'auto',
            paddingTop: '2.5mm',
            borderTop: '1px solid #cfcfcf',
            fontSize: '6.8pt',
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontWeight: 700 }}>
            Découvrez nos autres offres et bons plans sur Maxula.ma
          </div>
          <div className="coupon-ar" lang="ar" dir="rtl" style={{ fontSize: '7pt', fontWeight: 700 }}>
            اكتشفوا المزيد من العروض والخدمات والصفقات على منصتنا
          </div>

          <div style={{ marginTop: '1mm' }}>
            Vous proposez des services ? Rejoignez notre réseau de partenaires et
            développez votre visibilité avec Maxula.
          </div>
          <div className="coupon-ar" lang="ar" dir="rtl" style={{ fontSize: '7pt' }}>
            هل تقدمون خدمات؟ انضموا إلى شبكة شركاء مكسولا وطوّروا حضوركم ووصولكم إلى زبناء جدد.
          </div>

          <div
            style={{
              marginTop: '1.2mm',
              display: 'flex',
              alignItems: 'center',
              gap: '3mm',
              fontWeight: 700,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1mm' }}>
              <FiGlobe size={9} /> www.maxula.ma
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1mm' }}>
              <FiPhone size={9} /> 05 39 34 24 94
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1mm' }}>
              <FaWhatsapp size={9} /> 06 06 06 61 62
            </span>
          </div>
        </div>

        {/* Le bandeau de pied "Groupe MAXULA" a ete retire a la demande.
            Les coordonnees du service client restent presentes en haut a gauche
            (Service Clients + email + site) : le coupon garde donc un point de
            contact, ce qui compte s'il est offert a quelqu'un qui ne connait
            pas Maxula. */}
      </div>
    </div>
  );
};

export default CouponPrint;
